import { detectPitch, hzToNoteInfo, PitchResult } from './pitchDetection';

export interface PitchSample { t: number; p: PitchResult | null; }

// 2048-sample window, 1024-sample hop = ~43 frames/sec at 44.1kHz, matching
// the resolution of the live recorder's ghost overlay closely enough.
const WIN = 2048;
const HOP = 1024;
const YIELD_EVERY = 200; // yield to the UI every ~4.5s of source audio

// Slides a YIN pitch detector across a decoded AudioBuffer offline and returns
// samples compatible with the live-recording ghost overlay format.
// Multi-channel input is mono-mixed. Progress fires between chunks.
export async function extractContour(
  buffer: AudioBuffer,
  onProgress?: (pct: number) => void,
): Promise<PitchSample[]> {
  const sampleRate = buffer.sampleRate;
  const N = buffer.length;

  let mono: Float32Array;
  if (buffer.numberOfChannels === 1) {
    mono = buffer.getChannelData(0);
  } else {
    mono = new Float32Array(N);
    const chans = Array.from(
      { length: buffer.numberOfChannels },
      (_, i) => buffer.getChannelData(i),
    );
    for (let i = 0; i < N; i++) {
      let sum = 0;
      for (const c of chans) sum += c[i];
      mono[i] = sum / chans.length;
    }
  }

  if (N < WIN) {
    onProgress?.(1);
    return [];
  }

  const totalHops = Math.floor((N - WIN) / HOP) + 1;
  const out: PitchSample[] = new Array(totalHops);

  for (let i = 0; i < totalHops; i++) {
    const start = i * HOP;
    const win = mono.subarray(start, start + WIN);
    const raw = detectPitch(win, sampleRate);
    const t = (start / sampleRate) * 1000;
    if (raw) {
      const info = hzToNoteInfo(raw.hz);
      out[i] = { t, p: { ...info, clarity: raw.clarity } };
    } else {
      out[i] = { t, p: null };
    }
    if (i % YIELD_EVERY === 0) {
      onProgress?.(i / totalHops);
      await new Promise(r => setTimeout(r, 0));
    }
  }
  onProgress?.(1);
  return out;
}
